import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import authService from "@/services/authService";

class AuthGuard {
  constructor() {
    this.authStateReady = false;
    this.authStatePromise = null;
  }

  // Wait for Firebase auth state to be ready
  async waitForAuthState() {
    console.log(
      "[AuthGuard] waitForAuthState called, authStateReady:",
      this.authStateReady
    );

    if (this.authStateReady) {
      console.log("[AuthGuard] Auth state already ready");
      return Promise.resolve();
    }

    if (this.authStatePromise) {
      console.log("[AuthGuard] Auth state promise already exists, waiting...");
      return this.authStatePromise;
    }

    console.log("[AuthGuard] Creating new auth state promise");
    this.authStatePromise = new Promise((resolve) => {
      // Add a timeout fallback in case Firebase doesn't respond
      const timeout = setTimeout(() => {
        console.log("[AuthGuard] Auth state timeout, proceeding anyway");
        this.authStateReady = true;
        resolve();
      }, 3000); // 3 second timeout

      const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
        console.log(
          "[AuthGuard] Auth state changed, user:",
          user ? "logged in" : "not logged in"
        );
        clearTimeout(timeout);
        this.authStateReady = true;
        unsubscribe();
        resolve();
      });
    });

    return this.authStatePromise;
  }

  // Main authentication guard
  async guard(to, from, next) {
    try {
      console.log(
        "[AuthGuard] Guard called for route:",
        to.path,
        "from:",
        from.path
      );

      // Initialize auth service
      console.log("[AuthGuard] Initializing auth service...");
      await authService.initialize();

      // Wait for Firebase auth state
      console.log("[AuthGuard] Waiting for auth state...");
      await this.waitForAuthState();

      // Check if route requires authentication
      const requiresAuth = to.matched.some((route) => route.meta.requiresAuth);
      console.log("[AuthGuard] Route requires auth:", requiresAuth);

      if (!requiresAuth) {
        console.log("[AuthGuard] Route does not require auth, proceeding");
        next();
        return;
      }

      // Check if user is authenticated
      const user = authService.getCurrentUser();
      console.log(
        "[AuthGuard] Current user:",
        user ? "authenticated" : "not authenticated"
      );

      if (!user) {
        // No user - redirect to login
        console.log("[AuthGuard] No user, redirecting to /login");
        next("/login");
        return;
      }

      // User is authenticated - TEMPORARY: Skip profile status check until backend is deployed
      try {
        console.log(
          "[AuthGuard] TEMPORARILY skipping profile status check - backend not deployed yet"
        );

        // TODO: Uncomment when backend is deployed
        // const profileStatus = await authService.checkProfileStatus();
        //
        // switch (profileStatus.status) {
        //   case "new_user":
        //   case "incomplete_profile":
        //     // Redirect to complete profile
        //     if (to.path !== "/register") {
        //       next("/register");
        //       return;
        //     }
        //     break;
        //
        //   case "complete_profile": {
        //     // Store complete user data
        //     const userData = {
        //       ...profileStatus.userData,
        //       authToken: await authService.getIdToken(),
        //       appCheckToken: authService.getAppCheckToken(),
        //     };
        //
        //     localStorage.setItem("userData", JSON.stringify(userData));
        //     if (profileStatus.role) {
        //       // Store role if needed for admin routes
        //       localStorage.setItem("session", profileStatus.role);
        //     }
        //     break;
        //   }
        //
        //   default:
        //     console.error(
        //       "[AuthGuard] Unknown profile status:",
        //       profileStatus.status
        //     );
        //     next("/login");
        //     return;
        // }
        //
        // // Check role-based access
        // const requiredRole = to.meta.requiredRole;
        // if (requiredRole && profileStatus.role) {
        //   if (Array.isArray(requiredRole)) {
        //     if (!requiredRole.includes(profileStatus.role)) {
        //       next("/dashboard");
        //       return;
        //     }
        //   } else if (profileStatus.role !== requiredRole) {
        //     next("/dashboard");
        //     return;
        //   }
        // }

        // For now, just let authenticated users through
        console.log("[AuthGuard] User authenticated, proceeding to route");
        next();
      } catch (profileError) {
        console.error("[AuthGuard] Authentication error:", profileError);
        // On error, redirect to login
        next("/login");
      }
    } catch (error) {
      console.error("[AuthGuard] Authentication error:", error);
      next("/login");
    }
  }

  // Handle redirects from login page
  async handleLoginRedirect(to, from, next) {
    try {
      console.log("[AuthGuard] handleLoginRedirect called for:", to.path);

      await authService.initialize();
      await this.waitForAuthState();

      const user = authService.getCurrentUser();
      console.log(
        "[AuthGuard] handleLoginRedirect - user:",
        user ? "authenticated" : "not authenticated"
      );

      if (user) {
        // User is authenticated - check where to redirect
        console.log(
          "[AuthGuard] User authenticated, checking profile status..."
        );
        try {
          // TEMPORARY: Skip backend profile check until deployed
          console.log(
            "[AuthGuard] TEMPORARILY skipping profile status check - backend not deployed yet"
          );
          console.log(
            "[AuthGuard] User authenticated but staying on login page"
          );
          next();
          return;

          // TODO: Uncomment when backend is deployed
          // const profileStatus = await authService.checkProfileStatus();
          // console.log("[AuthGuard] Profile status:", profileStatus);
          //
          // switch (profileStatus.status) {
          //   case "new_user":
          //   case "incomplete_profile":
          //     next("/register");
          //     return;
          //
          //   case "complete_profile":
          //     next("/dashboard");
          //     return;
          //
          //   default:
          //     // Stay on login for unknown status
          //     next();
          //     return;
          // }
        } catch (error) {
          console.error("[AuthGuard] Profile check error on login:", error);
          // Stay on login page if profile check fails
          next();
        }
      } else {
        // No user - stay on login
        next();
      }
    } catch (error) {
      console.error("[AuthGuard] Login redirect error:", error);
      next();
    }
  }
}

// Export singleton instance
const authGuard = new AuthGuard();
export default authGuard;
